import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "../lib/supabase.ts";
import { canClearUp, emptyAnswer, withDeadline } from "../lib/requests.ts";
import { countryNames, groupZones, tidy, timeZones } from "../lib/places.ts";

export interface Venue { id: string; name: string; city: string | null; country: string | null; timezone: string }

const VENUE_TYPES: [string, string][] = [
  ["mall_food_court", "Mall food court"], ["food_hall", "Food hall"], ["airport", "Airport"],
  ["university", "University"], ["hospital", "Hospital"], ["hotel", "Hotel"],
  ["retirement", "Retirement community"], ["fast_food_restaurant", "Fast food restaurant"], ["other", "Other"],
];

// supabase-js reports a dropped connection in the browser's own words, which
// looked like a crash on this screen: "TypeError: Failed to fetch" (BUG-6).
const offline = (message: string) => /failed to fetch|networkerror|load failed|network request failed/i.test(message);

// A short form, not forty fields. The rest of the columns exist in the schema
// and are nullable; the form grows when someone needs them. Unknown stays
// unknown: every numeric field submits null when blank rather than zero, which
// is the difference between "not recorded" and "this venue has no seats".
//
// Every signed-in team member sees every venue. Only an admin sees the create
// form. Hiding it is a courtesy, not the control: the database refuses a venue
// from anyone who is not an admin, whatever this screen shows.
export default function Venues({ onPick, canCreate, userId }: {
  onPick: (v: Venue) => void;
  canCreate: boolean;
  userId: string;
}) {
  const [venues, setVenues] = useState<Venue[] | null>(null);
  // A failed first load. `retry` decides whether it tries again on its own: no
  // signal, a stall or a gateway fault can clear up; a refusal will not.
  const [loadErr, setLoadErr] = useState<{ message: string; retry: boolean } | null>(null);
  const [trying, setTrying] = useState(false);
  // One load at a time, like the sync loop. A load asked for while one is
  // running runs once more after it, so a refresh after creating a venue is
  // never lost behind a retry that started before the insert, and results
  // always land in the order their loads began.
  const inFlight = useRef(false);
  const again = useRef(false);
  const loaded = useRef(false);
  const [query, setQuery] = useState("");
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formErr, setFormErr] = useState<string | null>(null);
  const [venueType, setVenueType] = useState("");
  const zones = useMemo(() => timeZones(), []);
  const countries = useMemo(() => countryNames(), []);

  // Every load is cut off at 15 seconds, and the cut-off covers the sign-in
  // renewal every query waits on first. In a dead zone a request can stall
  // rather than fail, and a stalled first load used to leave "Loading
  // venues..." with nothing to press; now it becomes the error, with Try again.
  // A sign-in that could not be renewed is an error too, never an empty list:
  // the database answers an anonymous read with no rows, which this screen
  // showed as "No venues yet" (verifier finding on PR #9). Once a list has
  // loaded, a failed refresh leaves it on screen.
  const loadOnce = useCallback(async () => {
    const failed = (e: { message: string; code?: string }, status?: number) => {
      if (loaded.current) return;
      const retry = canClearUp(e, status);
      setLoadErr({ message: retry ? "Could not load venues. Check your signal and try again." : e.message, retry });
    };
    try {
      const { data, error, status } = await withDeadline((signal) => supabase
        .from("venues").select("id,name,city,country,timezone").is("merged_into", null).order("name")
        .abortSignal(signal));
      if (error) { failed(error, status); return; }
      // A success with no body is not an empty list.
      if (data == null) { failed(emptyAnswer); return; }
      loaded.current = true;
      setVenues(data);
      setLoadErr(null);
    } catch (e) {
      failed({ message: e instanceof Error ? e.message : String(e) });
    }
  }, []);

  const load = useCallback(async () => {
    if (inFlight.current) { again.current = true; return; }
    inFlight.current = true;
    setTrying(true);
    try {
      do {
        again.current = false;
        await loadOnce();
      } while (again.current);
    } finally {
      inFlight.current = false;
      setTrying(false);
    }
  }, [loadOnce]);
  useEffect(() => { void load(); }, [load]);

  // The list used to load once and stay stuck on its error after signal came
  // back (BUG-6). A failure that can clear up now tries again by itself: when
  // the browser reports it is online, when the app comes back to the screen,
  // and every 15 seconds. The timer is the one that matters in a dead zone,
  // where the browser still reports online throughout and the online event
  // never fires. A try asked for while one runs is folded into it, not stacked.
  const retrying = loadErr?.retry === true;
  useEffect(() => {
    if (!retrying) return;
    const tryNow = () => { void load(); };
    const onVisible = () => { if (document.visibilityState === "visible") tryNow(); };
    const timer = setInterval(tryNow, 15000);
    addEventListener("online", tryNow);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(timer);
      removeEventListener("online", tryNow);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [retrying, load]);

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!venues || q === "") return venues;
    return venues.filter((v) =>
      [v.name, v.city ?? "", v.country ?? ""].some((s) => s.toLowerCase().includes(q)));
  }, [venues, query]);

  async function create(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    const bad: string[] = [];
    const num = (k: string, label: string) => {
      const raw = String(f.get(k) ?? "").trim();
      // Number("") is 0, and 0 is a legal seat count. Blank must stay null or
      // the venue silently satisfies a "under 400 seats" filter. Anything that
      // is not a whole number is refused here: "about 300" used to become NaN,
      // which JSON sends as null, so a typed answer was quietly stored as unknown.
      if (raw === "") return null;
      if (!/^\d+$/.test(raw)) { bad.push(label); return null; }
      return Number(raw);
    };
    const counts = {
      seats: num("seats", "Seats"), tables: num("tables", "Tables"),
      num_vendors: num("num_vendors", "Food service providers"),
    };
    if (bad.length > 0) {
      setFormErr(`${bad.join(", ")}: a whole number, or blank if unknown.`);
      return;
    }
    const timezone = String(f.get("timezone") ?? "");
    // A backstop: the select is required, so browsers normally refuse first,
    // in their own words. This covers one that submits it empty anyway.
    if (!timezone.includes("/")) {
      setFormErr("Choose the venue's time zone.");
      return;
    }
    const type = String(f.get("venue_type") ?? "");
    const typeOther = type === "other" ? tidy(String(f.get("venue_type_other") ?? "")) : "";
    if (type === "other" && typeOther === "") {
      setFormErr("Say what kind of venue it is.");
      return;
    }
    const now = Date.now();
    setSaving(true);
    const { error } = await supabase.from("venues").insert({
      name: tidy(String(f.get("name") ?? "")),
      timezone,
      city: tidy(String(f.get("city") ?? "")) || null,
      country: tidy(String(f.get("country") ?? "")) || null,
      venue_type: type || null,
      venue_type_other: typeOther || null,
      ...counts,
      // Who registered it, which the column existed for and never got (DATA-1).
      registered_by: userId,
      created_at: now, updated_at: now,
    });
    setSaving(false);
    if (error) {
      // The raw constraint text used to reach the screen (BUG-2).
      setFormErr(error.code === "23505"
        ? "A venue with this name is already registered in this city and country."
        : offline(error.message) ? "No connection. Move somewhere with signal and try again." : error.message);
      return;
    }
    setCreating(false); setFormErr(null); setVenueType(""); void load();
  }

  return (
    <div className="pad">
      <h2>Venues</h2>
      <p className="muted">Pick where you are observing.</p>

      {loadErr ? (
        <div className="empty">
          <p className="err">{loadErr.message}</p>
          <button className="primary" onClick={() => void load()}>Try again</button>
          {/* Space kept whether or not a try is running, so the screen does not
              jump under the admin's thumb every 15 seconds. */}
          <p className="muted" style={{ visibility: trying ? "visible" : "hidden" }}>Trying again...</p>
        </div>
      ) : venues === null ? (
        <p className="muted">Loading venues...</p>
      ) : venues.length === 0 ? (
        <div className="empty">
          <p><strong>No venues yet.</strong></p>
          <p className="muted">
            {canCreate ? "Add the first one below." : "An admin adds venues. Check back once they have."}
          </p>
        </div>
      ) : (
        <>
          <label className="search">
            <span className="sr-only">Search venues</span>
            <input type="search" value={query} onChange={(e) => setQuery(e.target.value)}
                   placeholder="Search by name, city or country" autoComplete="off" />
          </label>
          {shown && shown.length === 0 ? (
            <p className="muted">No venue matches "{query.trim()}".</p>
          ) : (
            <ul className="list">
              {shown!.map((v) => (
                <li key={v.id}>
                  <button className="row" onClick={() => onPick(v)}>
                    <span>{v.name}</span>
                    <small>{[v.city ?? "No city", v.country].filter(Boolean).join(", ")} · {v.timezone}</small>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </>
      )}

      {canCreate && (creating ? (
        <form onSubmit={create} className="card">
          <h3>New venue</h3>
          <label>Name<input name="name" required /></label>
          <label>Timezone
            {/* Required: every time of day in the data is the venue's own, and
                lunch versus dinner is the study's main axis. Every region is
                offered, grouped by continent. */}
            <select name="timezone" defaultValue="" required>
              {/* No default. A prefilled zone was the registering device's, so
                  an admin in Wellington adding a Chicago venue saved it as
                  Auckland unless they noticed, and lunch became dinner. */}
              <option value="" disabled>Choose the venue's time zone</option>
              {groupZones(zones).map(([region, list]) => (
                <optgroup key={region} label={region}>
                  {list.map((z) => <option key={z} value={z}>{z.replace(/_/g, " ")}</option>)}
                </optgroup>
              ))}
            </select>
          </label>
          <label>City<input name="city" /></label>
          <label>Country
            {countries.length > 0 ? (
              <select name="country" defaultValue="">
                <option value="">Not set</option>
                {countries.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            ) : (
              <input name="country" />
            )}
          </label>
          <label>Type
            {/* Blank by default: a type nobody chose would be recorded as known. */}
            <select name="venue_type" value={venueType} onChange={(e) => setVenueType(e.target.value)}>
              <option value="">Not set</option>
              {VENUE_TYPES.map(([v, label]) => <option key={v} value={v}>{label}</option>)}
            </select>
          </label>
          {venueType === "other" && (
            <label>What kind of venue?
              <input name="venue_type_other" maxLength={120} required placeholder="For example, stadium concourse" />
            </label>
          )}
          <label>Seats<input name="seats" inputMode="numeric" placeholder="Leave blank if unknown" /></label>
          <label>Tables<input name="tables" inputMode="numeric" placeholder="Leave blank if unknown" /></label>
          {/* Stored as num_vendors; the pilot's word for them is food service providers. */}
          <label>Food service providers<input name="num_vendors" inputMode="numeric" placeholder="Leave blank if unknown" /></label>
          <p className="muted">Blank means unknown, not zero.</p>
          {/* Next to the button, not above the form: at the top it sat off screen
              and pressing Create venue looked like it did nothing (BUG-1). */}
          {formErr && <p className="err" role="alert">{formErr}</p>}
          <button className="primary" disabled={saving}>{saving ? "Creating..." : "Create venue"}</button>
          <button type="button" disabled={saving}
                  onClick={() => { setCreating(false); setFormErr(null); setVenueType(""); }}>Cancel</button>
        </form>
      ) : (
        <div className="stack">
          <button className="primary" onClick={() => setCreating(true)}>Add a venue</button>
        </div>
      ))}
    </div>
  );
}
